import { DecimalPipe, NgClass, TitleCasePipe } from '@angular/common';
import { Component, computed, input } from '@angular/core';
import { RiskLevelColourMuted } from '@shared/types';
import { HlmIconComponent } from '@spartan-ng/icon';

@Component({
  selector: 'dxc-risk-levels-overview',
  imports: [NgClass, HlmIconComponent, TitleCasePipe, DecimalPipe],
  template: `
    <div
      class="h-[60px] flex items-center gap-6 rounded-md p-3 w-full justify-between {{
        riskBgMutedColour
      }}"
      [ngClass]="{
        'loading-div': loading(),
        'risk-indicators__header': inPdf()
      }"
    >
      <div class="flex items-center gap-2">
        <hlm-icon name="lucideShieldAlert" />
        <div class="flex flex-col">
          <p class="text-xs font-medium {{ riskTextMutedColour }}">
            {{ type() === 'case' ? 'Case' : 'Customer' }} risk
          </p>
          <div class="flex items-center gap-2">
            <p class="text-sm font-semibold">
              {{ riskLevel() | titlecase }}
            </p>
          </div>
        </div>
      </div>
      <div class="flex flex-col items-end">
        <p class="text-xs font-medium {{ riskTextMutedColour }}">
          Weighted average
        </p>
        <div class="flex items-center gap-2">
          <p class="text-sm font-semibold">
            {{ weightedAverageNumber() | number: '1.2-3' }}
          </p>
        </div>
      </div>
      <div class="flex flex-col items-end">
        <p class="text-xs font-medium {{ riskTextMutedColour }}">
          Overall risk
        </p>
        <div class="flex items-center gap-2">
          <p class="text-sm font-semibold">
            {{ overallRiskNumber() | number: '1.2-3' }}
          </p>
        </div>
      </div>
    </div>
  `
})
export class RiskLevelsOverviewComponent {
  public type = input<'case' | 'customer'>('case');
  public loading = input.required<boolean>();
  public inPdf = input<boolean>(false);
  public riskLevel = input.required<string>();
  public weightedAverage = input.required<string>();
  public overallRisk = input.required<string>();
  public isRiskOnCreation = input<boolean>(false);
  public weightedAverageNumber = computed(() => Number(this.weightedAverage()));
  public overallRiskNumber = computed(() => Number(this.overallRisk()));

  /**
   * @getter riskBgMutedColour
   * @description Determines the background color based on the risk level.
   */
  get riskBgMutedColour(): string {
    if (this.isRiskOnCreation()) {
      return 'bg-border';
    }

    const risk = this.riskLevel().toLowerCase();
    if (risk === 'low') {
      return RiskLevelColourMuted.low;
    }
    if (risk === 'medium') {
      return RiskLevelColourMuted.medium;
    }
    if (risk === 'high') {
      return RiskLevelColourMuted.high;
    }
    return 'bg-border';
  }

  /**
   * @getter riskTextMutedColour
   * @description Determines the text color based on the risk level.
   */
  get riskTextMutedColour(): string {
    if (this.isRiskOnCreation()) {
      return 'text-text-muted';
    }

    const risk = this.riskLevel().toLowerCase();
    if (risk === 'low') {
      return 'text-green-900 dark:text-green-400';
    }
    if (risk === 'medium') {
      return 'text-yellow-900 dark:text-yellow-400';
    }
    if (risk === 'high') {
      return 'text-red-900 dark:text-red-300';
    }
    return '';
  }
}
